'use client'

import React, { useState, useEffect, useRef } from 'react'
import { useAITerminal } from './AITerminal'

const VisualDemo: React.FC = () => {
  const [symbol, setSymbol] = useState('BTCUSDT')
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [orderType, setOrderType] = useState('MARKET')
  const [side, setSide] = useState('BUY')
  const [isAnimating, setIsAnimating] = useState(false)
  const [lastAction, setLastAction] = useState<string>('')
  
  const { registerComponent } = useAITerminal()
  
  // Refs for visual interactions
  const symbolRef = useRef<HTMLInputElement>(null)
  const quantityRef = useRef<HTMLInputElement>(null)
  const priceRef = useRef<HTMLInputElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const buyTabRef = useRef<HTMLButtonElement>(null)
  const sellTabRef = useRef<HTMLButtonElement>(null)

  // Visual interaction methods
  const highlightElement = async (ref: React.RefObject<HTMLElement | null>, duration = 1000) => {
    const element = ref.current
    if (!element) return
    
    const originalStyle = element.style.cssText
    
    // Add highlight animation
    element.style.cssText += `
      box-shadow: 0 0 20px 5px rgba(102, 126, 234, 0.8) !important;
      border: 2px solid #667eea !important;
      transition: all 0.3s ease !important;
      transform: scale(1.02) !important;
    `
    
    await new Promise(resolve => setTimeout(resolve, duration))
    
    // Restore original style
    element.style.cssText = originalStyle
  }

  const typeIntoInput = async (ref: React.RefObject<HTMLInputElement | null>, value: string, setter: (val: string) => void) => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 500)
    
    element.focus()
    setter('')
    
    // Type character by character
    for (let i = 0; i <= value.length; i++) {
      const currentValue = value.substring(0, i)
      setter(currentValue)
      await new Promise(resolve => setTimeout(resolve, 150))
    }
    
    element.blur()
  }

  const clickButton = async (ref: React.RefObject<HTMLButtonElement | null>) => {
    const element = ref.current
    if (!element) return
    
    await highlightElement(ref, 600)
    
    // Click animation
    element.style.transform = 'scale(0.95)'
    element.style.transition = 'transform 0.1s ease'
    
    setTimeout(() => {
      element.style.transform = ''
      element.click()
    }, 100)
  }

  // Visual order execution
  const executeVisualOrder = async (params: any) => {
    setIsAnimating(true)
    setLastAction(`🎬 AI is placing ${params.side} order for ${params.quantity} ${params.symbol}`)
    
    try {
      // Step 1: Switch to correct tab
      if (params.side === 'BUY' && side !== 'BUY') {
        setLastAction('🖱️ Clicking BUY tab...')
        await clickButton(buyTabRef as React.RefObject<HTMLButtonElement>)
        await new Promise(resolve => setTimeout(resolve, 500))
      } else if (params.side === 'SELL' && side !== 'SELL') {
        setLastAction('🖱️ Clicking SELL tab...')
        await clickButton(sellTabRef as React.RefObject<HTMLButtonElement>)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 2: Fill symbol
      if (params.symbol && params.symbol !== symbol) {
        setLastAction(`⌨️ Typing symbol: ${params.symbol}`)
        await typeIntoInput(symbolRef as React.RefObject<HTMLInputElement>, params.symbol, setSymbol)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 3: Fill quantity
      if (params.quantity) {
        setLastAction(`⌨️ Typing quantity: ${params.quantity}`)
        await typeIntoInput(quantityRef as React.RefObject<HTMLInputElement>, params.quantity.toString(), setQuantity)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 4: Fill price if limit order
      if (params.type === 'LIMIT' && params.price) {
        if (orderType !== 'LIMIT') {
          setOrderType('LIMIT')
          await new Promise(resolve => setTimeout(resolve, 300))
        }
        setLastAction(`⌨️ Typing price: ${params.price}`)
        await typeIntoInput(priceRef as React.RefObject<HTMLInputElement>, params.price.toString(), setPrice)
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      // Step 5: Submit order
      setLastAction('🚀 Submitting order...')
      await clickButton(submitRef as React.RefObject<HTMLButtonElement>)
      
      setLastAction(`✅ ${params.side} order completed successfully!`)
      
    } catch (error) {
      setLastAction(`❌ Error: ${error}`)
    } finally {
      setTimeout(() => {
        setIsAnimating(false)
        setLastAction('')
      }, 2000)
    }
  }

  // Register this component with AI Terminal and listen for visual order events
  useEffect(() => {
    registerComponent('visualDemo', {
      id: 'visualDemo',
      type: 'VisualDemo',
      name: 'Visual Trading Demo',
      capabilities: [
        {
          action: 'place_order',
          description: 'Place trading orders with visual animations',
          parameters: [
            { name: 'symbol', type: 'string', description: 'Trading symbol' },
            { name: 'side', type: 'string', description: 'BUY or SELL' },
            { name: 'quantity', type: 'number', description: 'Order quantity' },
            { name: 'price', type: 'number', description: 'Order price' },
            { name: 'type', type: 'string', description: 'Order type' }
          ]
        }
      ],
      methods: {
        place_order: executeVisualOrder,
        executeVisualOrder
      }
    })

    // Listen for visual place order events
    const handleVisualPlaceOrder = async (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('🎬 VisualDemo: Received visual place order event:', customEvent.detail)
      try {
        const result = await executeVisualOrder(customEvent.detail)
        
        // Send back the result
        const resultEvent = new CustomEvent('visualOrderResult', {
          detail: {
            success: true,
            message: `Visual order demonstration completed for ${customEvent.detail.side} ${customEvent.detail.quantity} ${customEvent.detail.symbol}`,
            orderId: `visual_${Date.now()}`,
            result,
            ...customEvent.detail,
            timestamp: new Date()
          }
        })
        window.dispatchEvent(resultEvent)
      } catch (error) {
        const errorEvent = new CustomEvent('visualOrderResult', {
          detail: {
            success: false,
            error: `Failed to execute visual order: ${error}`,
            ...customEvent.detail
          }
        })
        window.dispatchEvent(errorEvent)
      }
    }

    window.addEventListener('visualPlaceOrder', handleVisualPlaceOrder)

    return () => {
      window.removeEventListener('visualPlaceOrder', handleVisualPlaceOrder)
    }
  }, []) // Remove registerComponent from dependency array to prevent infinite loop

  return (
    <div style={{
      padding: '20px',
      background: 'rgba(0,0,0,0.1)',
      borderRadius: '12px',
      margin: '20px'
    }}>
      <h3>🎬 AI Visual Demo - Buy/Sell Panel</h3>
      <p>The AI can interact with these elements visually:</p>
      
      {/* Tab Selection */}
      {/* Animation Status */}
      {isAnimating && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid white',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span>{lastAction}</span>
        </div>
      )}
      
      <div style={{ marginBottom: '16px' }}>
        <button 
          ref={buyTabRef}
          className={`demo-tab ${side === 'BUY' ? 'active' : ''}`}
          onClick={() => setSide('BUY')}
          style={{
            padding: '8px 16px',
            marginRight: '8px',
            border: side === 'BUY' ? '2px solid #10b981' : '1px solid #ccc',
            borderRadius: '6px',
            background: side === 'BUY' ? '#10b981' : 'transparent',
            color: side === 'BUY' ? 'white' : 'inherit',
            cursor: 'pointer'
          }}
        >
          🟢 BUY
        </button>
        <button 
          ref={sellTabRef}
          className={`demo-tab ${side === 'SELL' ? 'active' : ''}`}
          onClick={() => setSide('SELL')}
          style={{
            padding: '8px 16px',
            border: side === 'SELL' ? '2px solid #ef4444' : '1px solid #ccc',
            borderRadius: '6px',
            background: side === 'SELL' ? '#ef4444' : 'transparent',
            color: side === 'SELL' ? 'white' : 'inherit',
            cursor: 'pointer'
          }}
        >
          🔴 SELL
        </button>
      </div>

      {/* Symbol Input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px' }}>Symbol:</label>
        <input
          ref={symbolRef}
          type="text"
          name="symbol"
          className="symbol-input"
          placeholder="Enter trading pair (e.g., BTCUSDT)"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* Quantity Input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px' }}>Quantity:</label>
        <input
          ref={quantityRef}
          type="number"
          name="quantity"
          className="quantity-input"
          placeholder="Enter amount to trade"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* Order Type Selector */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px' }}>Order Type:</label>
        <select
          name="orderType"
          className="order-type-select"
          value={orderType}
          onChange={(e) => setOrderType(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
        >
          <option value="MARKET">Market Order</option>
          <option value="LIMIT">Limit Order</option>
          <option value="STOP">Stop Order</option>
        </select>
      </div>

      {/* Price Input (for limit orders) */}
      {orderType === 'LIMIT' && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px' }}>Price:</label>
          <input
            ref={priceRef}
            type="number"
            name="price"
            className="price-input"
            placeholder="Enter limit price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
        </div>
      )}

      {/* Submit Button */}
      <button
        ref={submitRef}
        className="submit-button place-order-button"
        onClick={() => {
          console.log('Order placed:', { symbol, quantity, price, orderType, side })
          alert(`${side} order placed for ${quantity} ${symbol}`)
        }}
        style={{
          width: '100%',
          padding: '12px',
          background: side === 'BUY' ? '#10b981' : '#ef4444',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        {side === 'BUY' ? '🟢' : '🔴'} Place {side} Order
      </button>

      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        background: 'rgba(102, 126, 234, 0.1)',
        borderRadius: '6px',
        fontSize: '14px'
      }}>
        <strong>🤖 AI Can:</strong>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>✨ <strong>Highlight</strong> elements with glowing borders</li>
          <li>⌨️ <strong>Type</strong> character-by-character into inputs</li>
          <li>🖱️ <strong>Click</strong> buttons with visual feedback</li>
          <li>🎯 <strong>Select</strong> tabs and dropdowns</li>
          <li>🎬 <strong>Animate</strong> the entire form filling process</li>
        </ul>
      </div>

      <div style={{ 
        marginTop: '12px',
        fontSize: '12px',
        color: '#666'
      }}>
        Try saying: <strong>"Place a buy order for 0.5 BTCUSDT"</strong>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default VisualDemo 